<script setup>
    import IlluminatedButton from '@/components/buttons/IlluminatedButton.vue';
    import OutlineButton from './buttons/OutlineButton.vue';
    
    import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
    import { useMenuStore } from '@/stores/menuStore';
    import { storeToRefs } from 'pinia';
    import { gsap } from 'gsap';
    import SplitText from 'gsap/SplitText';

    import { useRuntimeConfig } from '#app';

    const config = useRuntimeConfig();


    const burgerActive = ref(false);
    const menuStore = useMenuStore();
    const { items: menuItems } = storeToRefs(menuStore);    
    let timeline = null;
    let splitInstance = null;

    let isMobile = ref(false);
    let resizeHandler = null;
    const isScrolled = ref(false);

    const handleScroll = () => {
        isScrolled.value = window.scrollY > 50;
    };

const initTimeline = () => {

    if (timeline) {
        timeline.kill(); // Kill existing timeline to prevent memory leaks
    }
    timeline = gsap.timeline({
        paused: true,
    });
    timeline.pause();
    // Revert any previous SplitText before creating a new one
    if (splitInstance && splitInstance.revert) {
        splitInstance.revert();
        splitInstance = null;
    }
    splitInstance = SplitText.create(".left-side nav a", { type: "chars, lines" })
    
    timeline.fromTo(splitInstance.chars, {
        filter: 'blur(50px)',
        scale: 2.5,
        opacity: 0,
    },{
        filter: 'blur(0px)',
        scale: 1,
        opacity: 1,
        duration: 0.75,
        stagger: 0.025,
        ease: 'power3.out',
    })

    .addLabel('charsIn')
    .to(splitInstance.lines.reverse(), {
        filter: 'blur(50px)',
        scale: 2.5,
        opacity: 0,
        duration: 0.75,
        stagger: 0.05,
        ease: 'power3.out',
    })
};

onMounted(async () => {
    await document.fonts.ready;

    isMobile.value = window.innerWidth < 767;
    if (isMobile.value) {
        initTimeline();
    }

    const handleResize = () => {
        const newIsMobile = window.innerWidth < 767;
        if (newIsMobile !== isMobile.value) {
            isMobile.value = newIsMobile;
            if (isMobile.value) {
                initTimeline();
            } else {
                if (timeline) {
                    timeline.kill(); // Kill timeline if no longer mobile
                    timeline = null;
                }
                if (splitInstance && splitInstance.revert) {
                    splitInstance.revert();
                    splitInstance = null;
                }
            }
        }
    };
    resizeHandler = handleResize;
    window.addEventListener('resize', resizeHandler, { passive: true });
    window.addEventListener('scroll', handleScroll);
});

    onBeforeUnmount(() => {
        window.removeEventListener('scroll', handleScroll);
        if (resizeHandler) window.removeEventListener('resize', resizeHandler);
        if (timeline) {
            timeline.kill();
            timeline = null;
        }
        if (splitInstance && splitInstance.revert) {
            splitInstance.revert();
            splitInstance = null;
        }
        // Ensure the class is removed when the component is unmounted
        document.body.classList.remove('no-scroll');
    });

    const handleLinkClick = () => {
        if (burgerActive.value) {
            burgerActive.value = false;
            document.body.classList.remove('no-scroll');
        }
    };

    const handleBurgerClick = () => {
        if (!burgerActive.value) {
            document.body.classList.add('no-scroll');
            if (timeline) {
                timeline.seek(0);
                timeline.tweenTo('charsIn');
            }
            burgerActive.value = !burgerActive.value;
        } else {
            if (timeline) {
                timeline.play();
            }
            document.body.classList.remove('no-scroll');
            setTimeout(() => {
                burgerActive.value = !burgerActive.value;
            }, 250); // Delay to allow the animation to complete
        }
    };

</script>

<template>
    <header 
        class="site-header" 
        :class="{
            'active': burgerActive,
            'scrolled': isScrolled
        }"
    >
        <NuxtLink class="logo"  to="/" @click="handleLinkClick" aria-label="Accueil"> 
           <svg width="315" height="107" viewBox="0 0 315 107" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M162.292 77.0247H151.799L151.435 2.25083H161.928L162.292 77.0247ZM314.322 1.58882H303.796H293.866L288.405 12.1148H303.863L304.061 52.067L254.411 0L254.775 76.6275H265.301L265.036 26.4804L314.686 78.4812L314.322 1.58882ZM181.921 39.7536C181.868 34.4586 183.389 29.2672 186.29 24.8373C189.191 20.4073 193.341 16.9382 198.216 14.8695C203.09 12.8007 208.469 12.2255 213.671 13.2167C218.872 14.2079 223.663 16.7209 227.435 20.4372C231.207 24.1536 233.791 28.906 234.859 34.0923C235.928 39.2787 235.433 44.6655 233.437 49.5702C231.441 54.4749 228.034 58.6767 223.648 61.6432C219.262 64.6098 214.093 66.2075 208.798 66.234C205.283 66.2602 201.797 65.5932 198.54 64.2712C195.283 62.9492 192.319 60.9981 189.816 58.5295C187.314 56.0609 185.323 53.1232 183.957 49.8844C182.59 46.6457 181.876 43.1694 181.854 39.6543L181.921 39.7536ZM171.395 39.7536C171.441 47.125 173.671 54.3171 177.805 60.4206C181.938 66.5241 187.789 71.2649 194.617 74.0434C201.444 76.8218 208.943 77.5133 216.164 76.0302C223.384 74.5472 230.003 70.9562 235.183 65.7115C240.363 60.4668 243.871 53.8038 245.264 46.5652C246.658 39.3266 245.873 31.8374 243.01 25.0447C240.147 18.252 235.334 12.4609 229.179 8.4036C223.025 4.34633 215.805 2.20515 208.434 2.25083C203.537 2.27251 198.692 3.25914 194.176 5.15432C189.66 7.0495 185.562 9.81607 182.116 13.2959C178.67 16.7757 175.944 20.9006 174.093 25.4347C172.242 29.9688 171.302 34.8232 171.328 39.7205L171.395 39.7536ZM10.5927 39.7536C10.5337 34.4493 12.0514 29.247 14.9534 24.8066C17.8553 20.3661 22.0107 16.8876 26.8925 14.8122C31.7742 12.7368 37.1625 12.1581 42.3737 13.1494C47.5848 14.1408 52.3841 16.6575 56.1628 20.3804C59.9416 24.1033 62.5295 28.8646 63.5983 34.0605C64.6671 39.2563 64.1687 44.6525 62.1662 49.5646C60.1638 54.4768 56.7474 58.6835 52.3507 61.6512C47.9539 64.619 42.7748 66.214 37.4702 66.234C33.9552 66.2602 30.4695 65.5932 27.2125 64.2712C23.9554 62.9492 20.9909 60.9981 18.4885 58.5295C15.9861 56.0609 13.9949 53.1232 12.6287 49.8844C11.2625 46.6457 10.5481 43.1694 10.5264 39.6543L10.5927 39.7536ZM0.0667462 39.7536C0.112714 47.125 2.34345 54.3171 6.47693 60.4206C10.6104 66.5241 16.461 71.2649 23.2888 74.0434C30.1166 76.8218 37.6148 77.5133 44.8356 76.0302C52.0563 74.5472 58.6752 70.9562 63.8551 65.7115C69.035 60.4668 72.5433 53.8038 73.9365 46.5652C75.3296 39.3266 74.5451 31.8374 71.6819 25.0447C68.8188 18.252 64.0056 12.4609 57.8512 8.4036C51.6968 4.34633 44.4775 2.20515 37.1062 2.25083C32.2088 2.27251 27.3638 3.25914 22.8479 5.15432C18.3321 7.0495 14.2341 9.81607 10.7881 13.2959C7.34206 16.7757 4.61564 20.9006 2.76464 25.4347C0.913646 29.9688 -0.025613 34.8232 0.000531139 39.7205L0.0667462 39.7536ZM118.169 50.2465H105.094L126.808 77.0247H139.817L118.169 50.2465ZM95.1312 44.6194V76.9916H84.6052V44.6194V2.48253H119.328C124.771 2.69833 129.919 5.01243 133.694 8.93993C137.469 12.8674 139.577 18.1035 139.577 23.551C139.577 28.9984 137.469 34.2345 133.694 38.162C129.919 42.0895 124.771 44.4036 119.328 44.6194H95.1312ZM129.357 23.5344C129.363 21.3024 128.617 19.1336 127.239 17.3777C126.307 16.1672 125.108 15.1879 123.736 14.5162C122.364 13.8445 120.855 13.4985 119.328 13.505H95.5946V33.5969H119.328C121.961 33.5969 124.487 32.5507 126.349 30.6885C128.212 28.8262 129.258 26.3004 129.258 23.6668L129.357 23.5344Z" fill="url(#paint0_linear_1465_30821)"/>
                <path class="logo-sub-title" d="M226.456 106.232H224.534L216.717 94.1876H216.639C216.658 94.508 216.681 94.8741 216.707 95.2861C216.734 95.6915 216.753 96.1263 216.766 96.5905C216.786 97.0482 216.796 97.5157 216.796 97.993V106.232H215.246V91.8926H217.159L224.946 103.897H225.015C225.002 103.668 224.985 103.345 224.966 102.926C224.946 102.501 224.926 102.047 224.907 101.563C224.894 101.073 224.887 100.618 224.887 100.2V91.8926H226.456V106.232Z" fill="#4498C9"/>
                <path class="logo-sub-title" d="M210.041 99.0425C210.041 100.147 209.9 101.154 209.619 102.063C209.338 102.966 208.92 103.744 208.364 104.398C207.815 105.045 207.131 105.545 206.314 105.898C205.497 106.251 204.549 106.428 203.47 106.428C202.358 106.428 201.387 106.251 200.557 105.898C199.733 105.545 199.046 105.042 198.497 104.388C197.955 103.734 197.546 102.952 197.271 102.044C197.003 101.135 196.869 100.128 196.869 99.0228C196.869 97.5582 197.111 96.2734 197.595 95.1684C198.079 94.0634 198.811 93.2035 199.792 92.5889C200.779 91.9743 202.015 91.667 203.499 91.667C204.918 91.667 206.111 91.971 207.079 92.5791C208.053 93.1872 208.789 94.0437 209.286 95.1488C209.789 96.2472 210.041 97.5451 210.041 99.0425ZM198.625 99.0425C198.625 100.259 198.798 101.311 199.145 102.201C199.491 103.09 200.021 103.776 200.733 104.26C201.453 104.744 202.365 104.986 203.47 104.986C204.581 104.986 205.49 104.744 206.196 104.26C206.909 103.776 207.435 103.09 207.775 102.201C208.115 101.311 208.285 100.259 208.285 99.0425C208.285 97.1921 207.896 95.747 207.118 94.7074C206.347 93.6612 205.14 93.1382 203.499 93.1382C202.388 93.1382 201.472 93.3768 200.753 93.8541C200.034 94.3314 199.498 95.0115 199.145 95.8942C198.798 96.7703 198.625 97.8197 198.625 99.0425Z" fill="#4498C9"/>
                <path class="logo-sub-title" d="M189.986 106.232V91.8926H191.654V106.232H189.986Z" fill="#4498C9"/>
                <path class="logo-sub-title" d="M185.045 102.417C185.045 103.267 184.832 103.993 184.407 104.595C183.989 105.19 183.4 105.644 182.642 105.958C181.883 106.272 180.991 106.429 179.964 106.429C179.422 106.429 178.908 106.402 178.424 106.35C177.941 106.298 177.496 106.223 177.091 106.125C176.685 106.026 176.329 105.906 176.021 105.762V104.163C176.512 104.366 177.11 104.555 177.816 104.732C178.522 104.902 179.261 104.987 180.033 104.987C180.752 104.987 181.36 104.892 181.857 104.702C182.354 104.506 182.73 104.228 182.985 103.869C183.247 103.503 183.377 103.065 183.377 102.555C183.377 102.064 183.269 101.655 183.054 101.329C182.838 100.995 182.478 100.694 181.975 100.426C181.478 100.152 180.798 99.8607 179.935 99.5534C179.327 99.3376 178.791 99.1022 178.326 98.8472C177.862 98.5857 177.473 98.2914 177.159 97.9645C176.845 97.6376 176.607 97.2583 176.443 96.8268C176.286 96.3952 176.208 95.9016 176.208 95.3458C176.208 94.5808 176.401 93.9269 176.786 93.3842C177.179 92.835 177.718 92.4165 178.405 92.1289C179.098 91.8346 179.892 91.6875 180.788 91.6875C181.553 91.6875 182.259 91.7594 182.907 91.9033C183.56 92.0471 184.159 92.24 184.701 92.4819L184.182 93.9139C183.665 93.6981 183.116 93.5183 182.534 93.3744C181.958 93.2306 181.363 93.1587 180.749 93.1587C180.134 93.1587 179.614 93.2502 179.189 93.4333C178.771 93.6098 178.451 93.8616 178.228 94.1885C178.006 94.5154 177.895 94.9045 177.895 95.3556C177.895 95.8591 177.999 96.2775 178.209 96.611C178.424 96.9445 178.764 97.242 179.229 97.5035C179.699 97.7585 180.321 98.0266 181.092 98.3078C181.936 98.6151 182.652 98.942 183.24 99.2885C183.828 99.6285 184.276 100.047 184.584 100.544C184.891 101.034 185.045 101.659 185.045 102.417Z" fill="#4498C9"/>
                <path class="logo-sub-title" d="M171.139 91.8926V101.171C171.139 102.184 170.933 103.087 170.521 103.878C170.116 104.669 169.501 105.293 168.677 105.751C167.853 106.202 166.82 106.428 165.578 106.428C163.806 106.428 162.456 105.947 161.527 104.986C160.605 104.025 160.145 102.74 160.145 101.132V91.8926H161.822V101.181C161.822 102.397 162.142 103.335 162.783 103.995C163.43 104.656 164.395 104.986 165.676 104.986C166.552 104.986 167.268 104.829 167.824 104.515C168.386 104.195 168.802 103.75 169.07 103.181C169.344 102.606 169.482 101.942 169.482 101.19V91.8926H171.139Z" fill="#4498C9"/>
                <path class="logo-sub-title" d="M149.574 106.232H147.906V91.8926H155.89V93.3637H149.574V98.6207H155.507V100.082H149.574V106.232Z" fill="#4498C9"/>
                <path class="logo-sub-title" d="M131.091 99.0817L134.906 91.8926H136.701L131.924 100.671V106.232H130.257V100.749L125.471 91.8926H127.295L131.091 99.0817Z" fill="#4498C9"/>
                <path class="logo-sub-title" d="M112.438 91.8926H116.498C118.289 91.8926 119.636 92.1607 120.539 92.6968C121.441 93.233 121.892 94.1451 121.892 95.4332C121.892 95.989 121.784 96.4892 121.569 96.9338C121.359 97.3719 121.052 97.7348 120.647 98.0225C120.241 98.3101 119.741 98.5096 119.146 98.6207V98.7188C119.774 98.8169 120.33 98.9967 120.813 99.2582C121.297 99.5198 121.676 99.8859 121.951 100.357C122.226 100.827 122.363 101.426 122.363 102.152C122.363 103.034 122.157 103.78 121.745 104.388C121.34 104.996 120.764 105.457 120.019 105.771C119.274 106.078 118.391 106.232 117.371 106.232H112.438V91.8926ZM114.105 98.0421H116.851C118.1 98.0421 118.963 97.8361 119.44 97.4242C119.924 97.0122 120.166 96.4107 120.166 95.6195C120.166 94.8022 119.878 94.2138 119.303 93.8541C118.734 93.4945 117.825 93.3147 116.576 93.3147H114.105V98.0421ZM114.105 99.4446V104.809H117.086C118.361 104.809 119.264 104.561 119.793 104.064C120.323 103.561 120.588 102.884 120.588 102.034C120.588 101.491 120.467 101.027 120.225 100.641C119.99 100.255 119.604 99.9611 119.068 99.7584C118.538 99.5492 117.829 99.4446 116.939 99.4446H114.105Z" fill="#4498C9"/>
                <defs>
                    <linearGradient id="paint0_linear_1465_30821" x1="0.000531133" y1="39.1578" x2="314.72" y2="39.1578" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#5AB1E3"/>
                        <stop offset="0.5" stop-color="#1D5990"/>
                        <stop offset="0.6" stop-color="#2170A8"/>
                        <stop offset="0.77" stop-color="#258EC6"/>
                        <stop offset="0.9" stop-color="#28A0D8"/>
                        <stop offset="1" stop-color="#29A6DF"/>
                    </linearGradient>
                </defs>
            </svg>
        </NuxtLink>

       <ClientOnly>
        <div class="burger" v-if="isMobile" @click="handleBurgerClick">
            <div class="line"></div>
            <div class="line"></div>
       </div>
       </ClientOnly>
        <div class="left-side" >
            <nav>
                <NuxtLink v-for="item in menuItems" :key="item.id" :to="item.url" @click="handleLinkClick">{{ item.title }}</NuxtLink>
            </nav>

            <OutlineButton
                small
                label="Démo" 
                link="/contact#formulaire" 
                :big-light="true"
                @click="handleLinkClick"
                target="default"
            />

             <IlluminatedButton 
                class="demo-button"
                label="Connexion" 
                :link="config.public.CRMurl" 
                :big-light="true"
                target="_blank"
            />
        </div>
        <div class="overlay"></div>
    </header>
</template>

<style lang="scss" scoped>
:global(body.no-scroll) {
    overflow: hidden;
}

.site-header{

    display: flex;
    justify-content: center;
    align-items: flex-end;
    position: fixed;
    border: 1px solid transparent;
    background: rgba(15, 21, 37, 0.0);
    backdrop-filter: blur(0);
    top: 0;
    left: 0;
    width: calc(100vw);
    height: 85px;
    z-index: 1000;
    margin: 25px 0 0;
    max-width: 100vw;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 0;

    transition: all 1s ease-out;


    width: calc(100vw - 40px);
    max-width: 1000px;
    border-radius: 100px;
    padding: 5px 7px 5px 20px;
    margin: 0px;
    margin: 10px 0 0;

    & .logo{
        width: 50%;
        max-width: 190px;
        margin: 0 auto;
        transition: max-width 1s ease-out, transform 0.5s ease-out;

        & svg {
            width: 100%;
            height: 100%;
            object-fit: contain;    
            object-position: center;
        }

        & .logo-sub-title{
            filter: blur(0);
            opacity: 1;
            transition: filter 0.3s ease-in-out, opacity 0.5s
        }
    }

    & .burger{
        position: absolute;
        left: 25px;
        top: 25px;
        width: 40px;
        height: 40px;
        transition: all 1s ease-out;

        & .line {
            width: 100%;
            height: 2px;
            background: $accent-color;
            margin: 10px auto;
            transition: all 0.3s ease-in-out;

            &:first-child {
                transform-origin: top left;
            }

            &:last-child {
                transform-origin: bottom left;
            }
        }
    }

    & .left-side {
        display: flex;
        align-items: center;
        gap: 35px;
        width: calc(100vw - 40px);
        position: fixed;
        top: 0;
        left: 0;
        flex-direction: column;
        gap: 75px;
        height: 0;
        overflow: hidden;
        transition: all 0.5s ease-out;
        padding: 0;
        z-index: -1;

        & nav {
            display: flex;
            gap: 35px;
            flex-direction: column;

             & a{
                color: $white;
                font-size: 25px;
                font-weight: $link-weight;
                text-transform: $link-transform;
                text-decoration: unset;
                text-align: center;

                &.contact {
                    font-weight: bold;
                    color: $accent-color;
                    position: relative;

                    &::before {
                        content: '';
                       
                    }
                }
            }

            span.separator{
                position: relative;
                left: 50%;
                top: 0%;
                transform: translateX(-50%);
                width: 50px;
                height: 1px;
                background-color: $accent-color;
            }
        }

        & .demo-button{
            width: fit-content;
            margin: auto 0 50px;
            
        }
    }
    
    &.scrolled {
        border: 1px solid #193452;
        background: rgba(15, 21, 37, 0.70);
        backdrop-filter: blur(10px);
        width: calc(100vw - 40px);
        max-width: 1000px;
        border-radius: 100px;
        padding: 5px 7px 5px 20px;
        margin: 0px;
        border-radius: 50px;
        margin: 10px 0 0;
        height: 60px;

        & .burger{
            top: 13px;
        }
        .logo{
            max-width: 128px;
            transform: translateY(15%);

            & .logo-sub-title{
                filter: blur(15px);
                opacity: 0;
            }
        }
    }

    &.active {
        margin: 10px 0 0;
        width: calc(100vw - 40px);

        & .burger{
            
            & .line {
                &:first-child {
                    transform: translate(5px, -6px) rotate(45deg) ; 
                }

                &:last-child {
                    transform: translate(6px, 10px) rotate(-45deg);
                }
            }
        }

        & .left-side{
            height: calc(100dvh - 20px);
            border-radius: 30px;
            border: 1px solid #193452;
            background: rgba(15, 21, 37, 0.95);
            backdrop-filter: blur(10px);
            padding: 150px 15px 25px;
        }
    }

}

@media (min-width: 767px) {
    .site-header{
        height: auto;
        padding: 30px 50px;
        justify-content: space-between;
        align-items: center;
        max-width: 100vw;

        & .logo{
            width: 100%;
            max-width: 190px;
            margin: 0;
        }
        
        &.burger{
            display: none;
        }

        & .left-side{
            background: unset;
            position: relative;
            top: unset;
            left: unset;
            flex-direction: row;
            gap: 35px;
            width: unset;
            height: unset;
            overflow: unset;
            padding: 0;
            padding: 5px 0 5px 35px;
            border-radius: 50px;
            margin-left: auto;

            & nav{
                flex-direction: row;
                gap: 35px;

                & a {
                    font-size: $link-size;
                }
                
                span.separator {
                    left: 0;
                    top: 0;
                    transform: translateY(8px);
                    width: 1px;
                    height: 15px;
                    position: relative;
                }
            }

            .demo-button{
                max-width: 180px;
                padding: 0;
                margin: 0;
            }
        }

        & .logo{
            display: flex;
        }

        &.scrolled{
        }
    } 
}
</style>
